import { css } from '@emotion/react';
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AIResponse from '../AIResponse/AIResponse';
import Box, { BoxProps } from '../Box/Box';
import Button from '../Button/Button';
import TextArea from '../Form/TextArea';
import { Icon } from '../Icon';
import MessageBubble from '../MessageBubble/MessageBubble';
import TagChip from '../TagChip/TagChip';
import { Tooltip } from '../Tooltip';
import { Heading, Text } from '../Typography';
import { Widget } from '../Widget';
import {
  closeButtonContent,
  containerStyles,
  receivedWrapperStyles,
  scrollToBottomButtonStyles,
  sentWrapperStyles,
} from './ChatWidget.styles';
import AskBuckButton from './components/AskBuckButton';
import ThinkingIndicator from './components/ThinkingIndicator';
import useChatScrollControl from './hooks/useChatScrollControl';

export type ChatWidgetMessage =
  | React.ReactElement<
      React.ComponentProps<typeof AIResponse>,
      typeof AIResponse
    >
  | React.ReactElement<
      React.ComponentProps<typeof MessageBubble>,
      typeof MessageBubble
    >;

export type SuggestedPrompt = {
  label: string;
  /**
   * Optional value to pass when clicked (defaults to label)
   */
  value?: string;
};

export type ChatWidgetProps = {
  /**
   * Title displayed in the chat widget header
   */
  title: string;
  /**
   * Optional tooltip copy rendered behind an info icon next to the title
   */
  titleTooltipContent?: ReactNode;
  /**
   * Array of messages to display
   */
  messages: ChatWidgetMessage[];
  /**
   * Callback when user submits a message
   */
  onSubmit: (value: string) => void;
  /**
   * Placeholder text for the input field
   */
  placeholder?: string;
  /**
   * Whether the chat widget is disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * ARIA label for accessibility
   */
  ariaLabel?: string;
  /**
   * Width of the chat panel
   */
  panelWidth?: number | string;
  /**
   * Whether the widget is expanded (controlled)
   */
  expanded?: boolean;
  /**
   * Default expanded state (uncontrolled)
   */
  defaultExpanded?: boolean;
  /**
   * Callback when expanded state changes
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * Whether to show thinking indicator
   */
  isThinking?: boolean;
  /**
   * Text to display in thinking state
   * @default "Thinking..."
   */
  thinkingText?: React.ReactNode;
  /**
   * Custom content to render instead of the default Spinner + thinkingText.
   * When provided and isThinking is true, this replaces the entire thinking row.
   */
  thinkingContent?: ReactNode;
  /**
   * Messages to show when chat is empty
   */
  emptyState?: ChatWidgetMessage[];
  /**
   * Additional props for the container
   */
  containerProps?: BoxProps;
  /**
   * Suggested prompts to show above the input field
   */
  suggestedPrompts?: SuggestedPrompt[];
  /**
   * Callback when a suggested prompt is clicked
   */
  onPromptClick?: (prompt: string) => void;
  /**
   * Title for the suggested prompts section
   * @default "Other Helpful Topics"
   */
  suggestedPromptsTitle?: string;
  /**
   * Number to display in notification badge on trigger button
   */
  notificationCount?: number;
  /**
   * Whether to close the widget when clicking outside
   * @default true
   */
  closeOnClickOutside?: boolean;
  /**
   * Whether to enable smart auto-scroll behavior and show the scroll-to-bottom button
   * @default true
   */
  enableScrollToBottomControl?: boolean;
};

const DEFAULT_EMPTY_STATE: ChatWidgetMessage[] = [
  <AIResponse
    key="default-empty"
    showDisclaimer={false}
    showHelpfulQuestion={false}
    title="Have a question?"
  >
    I can answer questions about this package—species, seasons, rules, access,
    and more. What would you like to know?
  </AIResponse>,
];

const ChatWidget = ({
  title,
  titleTooltipContent,
  messages,
  onSubmit,
  placeholder = 'Type your question',
  disabled = false,
  className,
  ariaLabel = 'Open Chat',
  panelWidth = 480,
  expanded,
  defaultExpanded = false,
  onExpandedChange,
  isThinking = false,
  thinkingText = 'Thinking...',
  thinkingContent,
  emptyState = DEFAULT_EMPTY_STATE,
  containerProps,
  suggestedPrompts,
  onPromptClick,
  suggestedPromptsTitle = 'Other Helpful Topics',
  notificationCount,
  closeOnClickOutside = true,
  enableScrollToBottomControl = true,
}: ChatWidgetProps) => {
  const [value, setValue] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const isControlled = typeof expanded === 'boolean';
  const [internalExpanded, setInternalExpanded] =
    useState<boolean>(defaultExpanded);
  const isExpanded = isControlled ? (expanded as boolean) : internalExpanded;
  const {
    scrollRef,
    showScrollButton,
    scrollToBottom,
    markUserMessageSubmitted,
    saveCurrentScrollPosition,
  } = useChatScrollControl({
    messages,
    isThinking,
    isExpanded,
    enableScrollToBottomControl,
  });

  const setExpanded = useCallback(
    (next: boolean) => {
      // Save scroll position before closing
      if (!next) saveCurrentScrollPosition();
      if (!isControlled) setInternalExpanded(next);
      onExpandedChange?.(next);
    },
    [isControlled, onExpandedChange, saveCurrentScrollPosition]
  );

  useEffect(() => {
    if (isExpanded) {
      requestAnimationFrame(() => textAreaRef.current?.focus());
    }
  }, [isExpanded]);

  const messagesToRender = messages.length === 0 ? emptyState : messages;
  const shouldRenderSuggestedPrompts =
    messages.length === 0 && !!suggestedPrompts?.length;
  const firstAIResponseIndex = messagesToRender.findIndex(
    (element) => element.type === AIResponse
  );

  const renderedMessages = useMemo(
    () =>
      messagesToRender.map((element, index) => {
        const key = (element.key ?? index) as React.Key;
        if (element.type === AIResponse) {
          const aiElement = element as React.ReactElement<
            React.ComponentProps<typeof AIResponse>,
            typeof AIResponse
          >;
          const shouldShowTitleOnElement =
            index === firstAIResponseIndex
              ? aiElement.props.showTitle ?? true
              : false;

          return (
            <Box key={key} css={receivedWrapperStyles}>
              {React.cloneElement(aiElement, {
                showTitle: shouldShowTitleOnElement,
              })}
            </Box>
          );
        }
        if (element.type === MessageBubble) {
          return (
            <Box key={key} css={sentWrapperStyles}>
              {element}
            </Box>
          );
        }
        // Fallback: render as-is without alignment wrapper
        return <React.Fragment key={key}>{element}</React.Fragment>;
      }),
    [messagesToRender, firstAIResponseIndex]
  );

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    markUserMessageSubmitted();
    onSubmit(trimmed);
    setValue('');
  };

  return (
    <Widget
      ariaLabel={ariaLabel}
      icon="ConversationalSearchAi"
      expanded={isExpanded}
      onExpandedChange={setExpanded}
      panelWidth={panelWidth}
      className={className}
      containerProps={containerProps}
      closeOnClickOutside={closeOnClickOutside}
    >
      <Box mt={2}>
        <AskBuckButton badgeCount={notificationCount} />
      </Box>
      <Widget.Panel>
        <Box css={containerStyles}>
          <Box position="sticky" top={0} zIndex={1} mb="var(--spacing-4)">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap="var(--spacing-4)"
            >
              <Box display="flex" alignItems="center" gap="var(--spacing-4)">
                <Box display="flex" alignItems="center" gap="var(--spacing-4)">
                  <Heading
                    size="2xs"
                    fontWeight="bold"
                    color="var(--text-primary)"
                  >
                    {title}
                  </Heading>
                  {titleTooltipContent && (
                    <Tooltip content={titleTooltipContent} position="bottom">
                      <Box
                        display="flex"
                        alignItems="center"
                        aria-label="Show title information"
                      >
                        <Icon variant="CircleInfo" size="medium" />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <Button
                variant="text"
                size="xs"
                aria-label="Close widget"
                onClick={() => setExpanded(false)}
              >
                <span css={closeButtonContent}>
                  <Icon variant="Xmark" size="medium" />
                  <span>Close</span>
                </span>
              </Button>
            </Box>
          </Box>

          <Box position="relative" flex="1 1 auto" minHeight={0}>
            <Box
              ref={scrollRef}
              height="100%"
              overflowY="auto"
              p={0}
              pb={4}
              display="flex"
              flexDirection="column"
              gap="var(--spacing-2)"
              css={css`
                overscroll-behavior: contain;
              `}
            >
              <Box display="flex" flexDirection="column" gap="var(--spacing-4)">
                {renderedMessages}
              </Box>
              {isThinking &&
                (thinkingContent ? (
                  <Box css={receivedWrapperStyles}>{thinkingContent}</Box>
                ) : (
                  <ThinkingIndicator thinkingText={thinkingText} />
                ))}
              {shouldRenderSuggestedPrompts && (
                <Box css={receivedWrapperStyles}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="var(--spacing-4)"
                  >
                    <Text
                      size="md"
                      fontWeight="bold"
                      color="var(--text-primary)"
                    >
                      {suggestedPromptsTitle}
                    </Text>
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      gap="var(--spacing-4)"
                      alignItems="flex-start"
                    >
                      {suggestedPrompts.map((prompt, index) => (
                        <TagChip
                          key={index}
                          variant="success"
                          onClick={() =>
                            onPromptClick?.(prompt.value ?? prompt.label)
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          {prompt.label}
                        </TagChip>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
            {enableScrollToBottomControl && (
              <Box
                css={scrollToBottomButtonStyles}
                className={showScrollButton ? 'visible' : undefined}
              >
                <Button
                  variant="secondary"
                  size="xs"
                  icon={<Icon variant="AngleDownSharp" />}
                  onClick={() => scrollToBottom('smooth')}
                  aria-label="Scroll to bottom"
                  css={css`
                    background-color: var(--color-base-white);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                  `}
                />
              </Box>
            )}
          </Box>

          <Box position="sticky" bottom={0} zIndex={1} p={0}>
            <TextArea
              ref={textAreaRef}
              rows={3}
              value={value}
              maxHeight={250}
              autoExpand
              onChange={(e) => setValue(e.target.value)}
              showSubmit
              onSubmit={handleSubmit}
              disabled={disabled}
              submitDisabled={!value.trim() || isThinking}
              placeholder={placeholder}
            />
          </Box>
        </Box>
      </Widget.Panel>
    </Widget>
  );
};

export default ChatWidget;
